import { GeoJsonLayer } from '@deck.gl/layers/typed';
import { Box, styled, useTheme } from '@mui/material';
import { BBox2d, MultiPolygon } from '@turf/helpers/dist/js/lib/geojson';
import {
  Feature,
  FeatureCollection,
  Polygon,
  bbox,
  center,
  centroid,
  circle,
  featureCollection,
  geometry,
  geometryCollection,
  multiPolygon,
  polygon,
} from '@turf/turf';
import { useDialog } from 'components/hooks';
import { ThemeTypography } from 'designSystem';
import CustomMap, {
  ICustomMapRef,
  IMapMarker,
  IMapPopup,
  MapMarkerClickEvent,
} from 'designSystem/Map/CustomMap';
import { calculateArea, radiusFromHectares } from 'designSystem/Map/utils/map.utils';
import { TIconName } from 'designSystem/Primitives/Icon/Icon';
import uniqBy from 'lodash/uniqBy';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ERiskLevel, TRiskLevel } from 'types/compliance.types';
import { IGeoDataIssue } from 'types/geodata.types';
import { ImageVariant } from 'types/media.types';
import { IPlot } from 'types/plot.types';
import { IFarmSite } from 'types/site.types';
import { convertHexToRGBarray } from 'utils';
import { v4 as uuid } from 'uuid';
import PlotInfoPopup from './PlotInfoPopup';
import round from 'lodash/round';

type IFarmPlotsProps = {
  plots: (IPlot & { risk?: TRiskLevel })[];
  /**
   * In edit mode the edit button is shown in the info popup
   * @default edit
   */
  mode?: 'edit' | 'view';
  /**
   * Shows the partner that own the cultivation areas
   */
  showOwner?: boolean;
  /**
   * Warnings that are shown in the info popup
   */
  issues?: IGeoDataIssue[];
  issueSeverity?: 'error' | 'warning';
  /**
   * Minimum height of the map
   * @default 600px
   */
  minHeight?: number;
  /**
   * If the map farms are from a dataset, the datasetId is used to show neighboring farms only inside the dataset
   */
  datasetId?: string;
  /**
   * The risk level to be shown on the plots info popup & the color of the plot on the map
   * This will override the warning severity
   * @default undefined
   */
  farmRiskLevels?: { farmId: string; riskLevel: TRiskLevel }[];
};

export interface IFeatureProperties {
  farmId: string;
  plotId: string;
  title: string;
  name: string;
  /** in square km */
  areaSizeHa: number;
  ownedBy?: {
    id: string;
    name: string;
    logo?: ImageVariant | null;
  };
  outputTitle?: string;
  // Center of this single cultivation area
  polygonCenter: [number, number];
  // Hide the action button in the info popup
  hideActionButton?: boolean;
  /**
   * Warnings for the plots of the cultivation area
   */
  issues?: IGeoDataIssue[];
  issueSeverity?: 'error' | 'warning';
  riskLevel?: TRiskLevel;
}

const FarmPlotsAreaSize = styled(Box)(({ theme }) => ({
  position: 'absolute',
  background: theme.custom.themeColors.grayScale[20],
  padding: theme.spacing(1),
  borderRadius: theme.spacing(0.5),
  zIndex: 3,
  right: 46,
  top: 10,
}));

const FarmPlots: FC<IFarmPlotsProps> = ({
  mode = 'edit',
  plots,
  showOwner,
  issues,
  minHeight = 600,
  issueSeverity,
  farmRiskLevels,
  datasetId,
}) => {
  const customMapRef = useRef<ICustomMapRef>(null);
  const { openDialog, closeDialog } = useDialog();
  const [infoPopup, setInfoPopup] = useState<IMapPopup>();
  const [mapZoom, setMapZoom] = useState<number>();

  const theme = useTheme();

  const uniqueFarms = useMemo(
    () =>
      uniqBy(
        plots.map(({ farm }) => farm),
        'id'
      ),
    [plots]
  );

  const handleEditFarm = useCallback(
    (farmId: string) => {
      // Make sure the map is not in fullscreen mode anymore
      customMapRef.current?.exitFullscreen();

      // Close all open popups
      closeDialog();
      setInfoPopup(undefined);

      const farm: IFarmSite | undefined = uniqueFarms.find(farm => farm.id === farmId);
      if (!farm) {
        throw new Error('Could not find farm');
      }
      openDialog({
        type: 'ADD_EDIT_SITE',
        props: { site: farm },
      });
    },
    [uniqueFarms, openDialog, closeDialog]
  );

  const handleEditFarmPlots = useCallback(
    (farmId: string) => {
      // Make sure the map is not in fullscreen mode anymore
      customMapRef.current?.exitFullscreen();

      // Close all open popups
      closeDialog();
      setInfoPopup(undefined);

      const farm: IFarmSite | undefined = uniqueFarms.find(farm => farm.id === farmId);
      if (!farm) {
        throw new Error('Could not find farm');
      }
      openDialog({
        type: 'ADD_EDIT_PLOTS',
        props: {
          farm,
          datasetId,
        },
      });
    },
    [uniqueFarms, datasetId, openDialog, closeDialog]
  );

  const getPlotHexColor = useCallback(
    (properties?: Pick<IFeatureProperties, 'riskLevel' | 'issues'>, opacity = 255) => {
      if (properties?.riskLevel) {
        return convertHexToRGBarray(
          properties?.riskLevel === ERiskLevel.HIGH
            ? theme.custom.themeColors.error[100]
            : properties?.riskLevel === ERiskLevel.MEDIUM
            ? theme.custom.themeColors.accent[100]
            : theme.custom.themeColors.success[100],
          opacity
        );
      }
      const warningColor =
        issueSeverity === 'error'
          ? theme.custom.themeColors.error[100]
          : theme.custom.themeColors.accent[100];

      return properties?.issues?.length
        ? convertHexToRGBarray(warningColor, opacity)
        : convertHexToRGBarray(theme.custom.colors.babyBlue, opacity);
    },
    [theme.custom, issueSeverity]
  );

  const handleFeatureClick = useCallback(
    (featureProperties: IFeatureProperties) => {
      setInfoPopup(prev => {
        if (prev && prev.id === featureProperties.plotId) {
          return undefined;
        }
        return {
          id: featureProperties.plotId,
          coordinate: featureProperties.polygonCenter,
          content: (
            <PlotInfoPopup
              showOwner={showOwner}
              infoItem={featureProperties}
              onEditFarmPlotsClick={handleEditFarmPlots}
              onEditFarmClick={handleEditFarm}
            />
          ),
        };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [infoPopup, setInfoPopup]
  );

  const handlePopupClose = useCallback(() => setInfoPopup(() => undefined), [setInfoPopup]);

  /**
   * This feature collections contains all the cultivation areas of the farm as single polygons with their properties
   * To be able to show the info popup when clicking on the cultivation area on a single polygon we need to have the center of each polygon
   * To be able to show centered markers of the one cultivation area we need to have the center of the multi polygon
   */
  const features: FeatureCollection<Polygon | MultiPolygon, IFeatureProperties> = useMemo(() => {
    const features: Feature<Polygon | MultiPolygon, IFeatureProperties>[] = plots.map(
      ({
        farmId,
        farm: { title, locationName, rawMaterial, ownedBy },
        id: plotId,
        feature,
        sizeHa,
      }) => {
        const issuesOfFarm = issues?.filter(issue => issue.siteId === farmId);
        const riskLevel = farmRiskLevels?.find(riskLevel => riskLevel.farmId === farmId)?.riskLevel;
        const featureGeometry = geometry(feature.geometry.type, feature.geometry.coordinates);
        const properties: IFeatureProperties = {
          plotId,
          farmId,
          name: locationName,
          title,
          outputTitle: rawMaterial?.title,
          ownedBy,
          issues: issuesOfFarm,
          issueSeverity,
          riskLevel,
          hideActionButton: mode === 'view',
          areaSizeHa: sizeHa || calculateArea(featureGeometry),
          // Center of the polygon always inside the polygon
          polygonCenter:
            feature.geometry.type === 'Point'
              ? feature.geometry.coordinates
              : (centroid(featureGeometry).geometry.coordinates as [number, number]),
        };
        if (feature.geometry.type === 'Polygon') {
          return polygon(feature.geometry.coordinates, properties, { id: uuid() });
        } else if (feature.geometry.type === 'MultiPolygon') {
          return multiPolygon(feature.geometry.coordinates, properties, { id: uuid() });
        } else if (feature.geometry.type === 'Point') {
          return circle(
            feature.geometry.coordinates,
            // 112.8 meters is the radius of the circle of 4ha
            sizeHa ? radiusFromHectares(sizeHa) : 112.8,
            {
              units: 'meters',
              properties,
            }
          );
        }
        throw new Error(`Cultivation area ${farmId} has an invalid geometry type`);
      }
    );
    return featureCollection(features);
  }, [plots, issueSeverity, issues, farmRiskLevels, mode]);

  const featureLayer = useMemo(
    () =>
      new GeoJsonLayer<IFeatureProperties>({
        id: 'farm-plots',
        data: features,
        getLineWidth: 2,
        pickable: true,
        getFillColor: feature => getPlotHexColor(feature?.properties as IFeatureProperties, 50),
        getLineColor: feature => getPlotHexColor(feature?.properties as IFeatureProperties),
        onClick: info => handleFeatureClick(info.object.properties),
      }),
    [features, handleFeatureClick, getPlotHexColor]
  );

  const pointFeatures: IMapMarker[] = useMemo(
    () =>
      plots
        .filter(({ feature }) => feature.geometry.type === 'Point')
        .map(({ id: plotId, feature, farmId }) => {
          const riskLevel = farmRiskLevels?.find(
            riskLevel => riskLevel.farmId === farmId
          )?.riskLevel;
          const issuesOfFarm = issues?.filter(issue => issue.siteId === farmId);
          const customIcon: TIconName = 'map-marker';
          return {
            id: plotId,
            coordinate: feature.geometry.coordinates as [number, number], // can only be a point here since we filtered for it,
            options: {
              customIcon,
              color: `rgba(${getPlotHexColor({ issues: issuesOfFarm, riskLevel })})`,
            },
          };
        }),
    [plots, issues, farmRiskLevels, getPlotHexColor]
  );

  // Event id is the id of the clicked plot
  const handleMarkerClick = useCallback(
    (event: MapMarkerClickEvent) => {
      if (!event.id) {
        console.error('No id found for the clicked marker');
        return;
      }
      // If the click was on a polygon marker, we need to find the polygon in the feature collection
      const polygonFeature = features.features.find(
        polygon => polygon.properties.plotId === event.id
      );
      if (polygonFeature) {
        customMapRef.current?.customFitBounds(bbox(polygonFeature) as BBox2d);
        handleFeatureClick(polygonFeature.properties);
      } else {
        const farm = plots.find(plot => plot.id === event.id)?.farm;
        if (!farm) {
          console.error(`Farm with id ${event.id} not found`);
          return;
        }
        const riskLevel = farmRiskLevels?.find(
          riskLevel => riskLevel.farmId === farm.id
        )?.riskLevel;
        const issuesOfFarm = issues?.filter(issue => issue.siteId === farm.id);
        handleFeatureClick({
          farmId: farm.id,
          plotId: event.id,
          name: farm.locationName,
          title: farm.title,
          outputTitle: farm.rawMaterial?.title,
          ownedBy: farm.ownedBy,
          areaSizeHa: farm.sizeHa || 0.04,
          issues: issuesOfFarm,
          issueSeverity: issueSeverity,
          riskLevel,
          polygonCenter: event.coordinate as [number, number],
          hideActionButton: mode === 'view',
        });
      }
    },
    [features, customMapRef, plots, issueSeverity, issues, farmRiskLevels, mode, handleFeatureClick]
  );

  /**
   * Markers are only shown when the zoom level is higher than 10 to identify the cultivation areas if the world is zoomed out
   */
  const markers: IMapMarker[] = useMemo(
    () =>
      mapZoom && mapZoom < 12
        ? features.features.reduce((prev, feature) => {
            const coordinate = feature.properties.polygonCenter;
            if (!coordinate) {
              return prev;
            }
            // If marker for that cultivation area was already added, don't add it again
            if (prev.findIndex(prevMarker => prevMarker.coordinate === coordinate) !== -1) {
              return prev;
            }
            return [
              ...prev,
              {
                id: feature.properties.plotId,
                coordinate,
                options: {
                  customIcon: 'map-marker',
                  color: `rgba(${getPlotHexColor(feature.properties)})`,
                  focusOnClick: false,
                },
              },
            ];
          }, [] as IMapMarker[])
        : [],
    [features, mapZoom, getPlotHexColor]
  );

  const totalAreaSizeHa = useMemo(() => calculateArea(features), [features]);

  /**
   * Center of all plots, this will be used for the initial view state from which point the map will zoom in to focus all available plots
   * Initial zoom level is 2 so the user can also identify the continent + country of the visible plots
   */
  const initialCenter = useMemo(() => {
    const allFeatures = [
      ...features.features.map(f => f.geometry),
      ...pointFeatures.map(f => geometry('Point', f.coordinate)),
    ];
    if (!allFeatures.length) {
      return undefined;
    }
    return center(geometryCollection(allFeatures));
  }, [features, pointFeatures]);

  const numberOfPlots = useMemo(() => features.features.length, [features]);

  useEffect(() => {
    customMapRef.current?.centerFeatures();
    setInfoPopup(undefined); // Close the info popup when data changed
  }, [customMapRef, pointFeatures, pointFeatures]);

  return (
    <Box width="100%" height="100%" position="relative" borderRadius={6}>
      <FarmPlotsAreaSize>
        <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_80">
          Total area
        </ThemeTypography>
        <ThemeTypography variant="BODY_SMALL">
          {round(totalAreaSizeHa, 2).toLocaleString('en-GB').replace(/,/g, ' ')} ha
        </ThemeTypography>
        {numberOfPlots >= 1 && (
          <Box mt={1}>
            <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_80">
              Nr of plots
            </ThemeTypography>
            <ThemeTypography variant="BODY_SMALL">{numberOfPlots}</ThemeTypography>
          </Box>
        )}
      </FarmPlotsAreaSize>
      <CustomMap
        initialViewState={
          initialCenter
            ? {
                latitude: initialCenter.geometry.coordinates[1],
                longitude: initialCenter.geometry.coordinates[0],
              }
            : undefined
        }
        customRef={customMapRef}
        markers={[...markers, ...pointFeatures]}
        layers={[featureLayer]}
        infoPopup={infoPopup}
        mapStyle="satellite"
        style={{
          minHeight,
          height: '100%',
          width: '100%',
          borderRadius: 6,
        }}
        config={{
          enableMapStyleToggle: true,
          enableCenterButton: true,
        }}
        onZoom={event => setMapZoom(event.viewState.zoom)}
        onMapLoad={map => setMapZoom(map.getZoom())}
        onMarkerPopupClose={handlePopupClose}
        onMarkerClick={handleMarkerClick}
      />
    </Box>
  );
};

export default FarmPlots;
